import type {
  AreaFormRow,
  FacilityCategory,
  FacilityFormRow,
  InsuranceFormData,
  UnitFormRow,
} from "../components/insurance/form/types";

export function toStr(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

export function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function toDate(v: unknown): string {
  if (v == null) return "";
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0] ?? "";
}

export function mapRecordToForm(r: Record<string, unknown>): InsuranceFormData {
  const damageRow = (r.damageProvisions as Record<string, unknown>[] | undefined)?.[0];
  return {
    insured: toStr(r.insured),
    contactFullName: toStr(r.contactFullName),
    contactFirstName: toStr(r.contactFirstName),
    contactAddress1: toStr(r.contactAddress1),
    contactAddress2: toStr(r.contactAddress2),
    contactPhone: toStr(r.contactPhone),
    contactEmail: toStr(r.contactEmail),
    address: toStr(r.address),
    latitude: toNum(r.latitude),
    longitude: toNum(r.longitude),
    buildingName: toStr(r.buildingName),
    numberOfUnits: Number(r.numberOfUnits) || 1,
    propertyTypeId: toStr(r.propertyTypeId),
    useOcc: toStr(r.useOcc),
    yearBuilt: toNum(r.yearBuilt),
    eyrBuilt: toNum(r.eyrBuilt),
    land: toStr(r.land),
    subsoilId: toStr(r.subsoilId),
    upgradeRequirements: toStr(r.upgradeRequirements),
    heritageListing: toStr(r.heritageListing),
    seismicGrade: toStr(r.seismicGrade),
    pocFrom: toDate(r.pocFrom),
    pocTo: toDate(r.pocTo),
    valuerPersonId: toStr(r.valuerId),
    feeAmountExGst: toNum(r.feeAmountExGst),
    accountNumberForXero: toStr(r.accountNumberForXero),
    assetDescription: toStr(r.assetDescription),
    construction: toStr(r.construction),
    recreationalFeatures: toStr(r.recreationalFeatures),
    retainingWalls: toStr(r.retainingWalls),
    specialFeatures: toStr(r.specialFeatures),
    features: toStr(r.features),
    detailedDescription: toStr(r.detailedDescription),
    sModal: toNum(r.sModal),
    eModal: toNum(r.eModal),
    percentChange: toNum(r.percentChange),
    age: toNum(r.age),
    feesPercentage: toNum(r.feesPercentage),
    demolitionPercentage: toNum(r.demolitionPercentage),
    gst: Number(r.gst) || 15,
    firstYearMonths: toNum(r.reinstatementMonths) ?? 12,
    inflationDemoPlanMonths: toNum(r.inflationDemoPlanMonths),
    inflationConstructionMonths: toNum(r.inflationConstructionMonths),
    inflationYear1: toNum(r.inflationYear1),
    inflationYear2: toNum(r.inflationYear2),
    inflationYear3: toNum(r.inflationYear3),
    inflationYear4: toNum(r.inflationYear4),
    certificateDate: toDate(r.certificateDate),
    updateMonth: toStr(r.updateMonth),
    damageAmount: toNum(r.damageAmount),
    damageChangePercentage: toNum(r.damageChangePercentage),
    damageFirstYearMonths: toNum(damageRow?.firstYearMonths),
    damageDemoPlanMonths: toNum(damageRow?.demoPlanMonths),
    damageConstructionMonths: toNum(damageRow?.constructionMonths),
    damageInflationYear1Pct: toNum(damageRow?.inflationYear1Pct),
    damageInflationYear2Pct: toNum(damageRow?.inflationYear2Pct),
    damageInflationYear3Pct: toNum(damageRow?.inflationYear3Pct),
    damageInflationYear4Pct: toNum(damageRow?.inflationYear4Pct),
    percentChangeOverride: false,
    ageOverride: false,
    fslText: toStr(r.fslText),
    note: toStr(r.note),
    excludeFromMailout: !!r.excludeFromMailout,
    isActive: r.isActive !== false,
    cancelled: !!r.cancelled,
    cancelledDate: toDate(r.cancelledDate),
    finalSentConfirmed: !!r.finalSentConfirmed,
    owner: toStr(r.owner),
    ownerAddress: toStr(r.ownerAddress),
    broker: toStr(r.broker),
    brokerAddress: toStr(r.brokerAddress),
    instructingParty: toStr(r.instructingParty),
    instructingEmail: toStr(r.instructingEmail),
    invoiceTo: toStr(r.invoiceTo),
    propertyCategory: toStr(r.propertyCategory),
  };
}

export function mapAreas(areas: Record<string, unknown>[]): AreaFormRow[] {
  const firstEyr = toNum(areas[0]?.eyrBuilt);
  return areas.map((a) => ({
    tempId: toStr(a.id) || crypto.randomUUID(),
    description: toStr(a.description),
    eyrBuilt: toNum(a.eyrBuilt),
    eyrFollowsFirst: a.eyrBuilt == null || toNum(a.eyrBuilt) === firstEyr,
    area: toNum(a.area),
    multiple: toNum(a.multiple),
    rate: toNum(a.rate),
    rateOverride: a.rate != null,
    addGst: a.addGst !== false,
    firstYearMonths: toNum(a.firstYearMonths),
    demoPlanMonths: toNum(a.demoPlanMonths),
    constructionMonths: toNum(a.constructionMonths),
    inflationYear1Pct: toNum(a.inflationYear1Pct),
    inflationYear2Pct: toNum(a.inflationYear2Pct),
    inflationYear3Pct: toNum(a.inflationYear3Pct),
    inflationYear4Pct: toNum(a.inflationYear4Pct),
    demolitionPercentage: toNum(a.demolitionPercentage),
    percentagePa: toNum(a.percentagePa),
    calcRate: toNum(a.rate),
    calcReinstatementExcl: toNum(a.calcReinstatementExcl),
    calcReinstatementIncl: toNum(a.calcReinstatementIncl),
    calcReinstatementMonths: toNum(a.calcReinstatementMonths),
    calcInflationAmtYear1: null,
    calcInflationAmtYear2: null,
    calcInflationAmtYear3: null,
    calcInflationAmtYear4: null,
    calcInflationTotal: toNum(a.calcInflationTotal),
    calcDemolitionCost: toNum(a.calcDemolitionCost),
    calcDemolitionRate: null,
    calcDepreciation: toNum(a.calcDepreciation),
    calcDepreciationTotalPct: null,
    calcDrcForFsl: toNum(a.calcDrcForFsl),
  }));
}

export function mapUnits(units: Record<string, unknown>[]): UnitFormRow[] {
  return units.map((u) => ({
    tempId: toStr(u.id) || crypto.randomUUID(),
    label: toStr(u.label),
    use: toStr(u.use) || "Residential",
    configuration: toStr(u.configuration),
    grossFloorArea: toNum(u.grossFloorArea),
    includedInEstimate: u.includedInEstimate !== false,
  }));
}

export function mapFacilities(facilities: Record<string, unknown>[]): FacilityFormRow[] {
  return facilities.map((f) => ({
    tempId: toStr(f.id) || crypto.randomUUID(),
    category: (toStr(f.category) || "RECREATIONAL") as FacilityCategory,
    description: toStr(f.description),
    area: toNum(f.area),
    multiple: toNum(f.multiple),
    rate: toNum(f.rate),
    rateOverride: f.rate != null,
    addGst: f.addGst !== false,
    firstYearMonths: null,
    demoPlanMonths: null,
    constructionMonths: null,
    inflationYear1Pct: toNum(f.inflationYear1Pct),
    inflationYear2Pct: toNum(f.inflationYear2Pct),
    inflationYear3Pct: toNum(f.inflationYear3Pct),
    inflationYear4Pct: toNum(f.inflationYear4Pct),
    demolitionPercentage: toNum(f.demolitionPercentage),
    calcRate: toNum(f.rate),
    calcReinstatementExcl: null,
    calcReinstatementIncl: toNum(f.calcReinstatementIncl),
    calcReinstatementMonths: null,
    calcInflationAmtYear1: null,
    calcInflationAmtYear2: null,
    calcInflationAmtYear3: null,
    calcInflationAmtYear4: null,
    calcInflationTotal: toNum(f.calcInflationTotal),
    calcDemolitionCost: toNum(f.calcDemolitionCost),
    calcDemolitionRate: null,
  }));
}
